import { addHours } from 'date-fns';
import { formatInTimeZone, fromZonedTime, getTimezoneOffset } from 'date-fns-tz';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const getCalendarDayParts = (day: Date, timeZone: string) => ({
  date: Number(formatInTimeZone(day, timeZone, 'd')),
  month: Number(formatInTimeZone(day, timeZone, 'M')) - 1,
  year: Number(formatInTimeZone(day, timeZone, 'yyyy')),
});

const getDaylightSavingTransitionTooltipValues = (day: Date, timeZone: string) => {
  const { date, month, year } = getCalendarDayParts(day, timeZone);

  const dayStart = fromZonedTime(new Date(year, month, date, 0, 0, 0), timeZone);
  const dayEnd = fromZonedTime(new Date(year, month, date, 23, 59, 59), timeZone);
  const offsetAtDayStart = formatInTimeZone(dayStart, timeZone, 'xxx');
  const offsetAtDayEnd = formatInTimeZone(dayEnd, timeZone, 'xxx');

  if (offsetAtDayStart === offsetAtDayEnd) {
    return null;
  }

  const initialOffset = offsetAtDayStart;

  for (let hours = 1; hours <= 24; hours++) {
    const currentHourStart = addHours(dayStart, hours);
    const offset = formatInTimeZone(currentHourStart, timeZone, 'xxx');

    if (offset === initialOffset) {
      continue;
    }

    const previousHourStart = addHours(dayStart, hours - 1);
    const hourBeforeTransition = Number(formatInTimeZone(previousHourStart, timeZone, 'H'));
    const hourAtTransition = Number(formatInTimeZone(currentHourStart, timeZone, 'H'));
    const isDaylightSavingStarting = getTimezoneOffset(offset) > getTimezoneOffset(initialOffset);

    const transitionHour = isDaylightSavingStarting ? hourAtTransition - 1 : hourBeforeTransition + 1;

    return {
      adjustedTime: new Date(year, month - 1, date, hourAtTransition),
      dayOfWeek: currentHourStart,
      isDaylightSavingStarting,
      transitionTime: new Date(year, month - 1, date, transitionHour),
    };
  }

  return null;
};

export const useDaylightSavingTransitionTooltip = (primaryTimezone: string) => {
  const { t } = useTranslation(['translationReactComponents']);

  return useCallback(
    (day: Date) => {
      const values = getDaylightSavingTransitionTooltipValues(day, primaryTimezone);

      if (!values) {
        return null;
      }

      const transitionTime = t('dateFormats.time', { date: values.transitionTime });
      const adjustedTime = t('dateFormats.time', { date: values.adjustedTime });
      const dayOfWeek = t('dateFormats.dayLong', { date: values.dayOfWeek });

      if (values.isDaylightSavingStarting) {
        return t(
          'Daylight Saving Time starts at {{transitionTime}} on {{dayOfWeek}}, and clocks are set forward to {{adjustedTime}}.',
          {
            adjustedTime,
            dayOfWeek,
            transitionTime,
          },
        );
      }

      return t(
        'Daylight Saving Time ends at {{transitionTime}} on {{dayOfWeek}}, and clocks are set back to {{adjustedTime}}.',
        {
          adjustedTime,
          dayOfWeek,
          transitionTime,
        },
      );
    },
    [primaryTimezone, t],
  );
};
